import { createSlice } from "@reduxjs/toolkit";
import { getState } from "@bd-stores/store";

/**
 * @type {BD_StoreGameProgress}
 */
const initialState = {
  players: [],
  finalTeamAnswers: {},
  teamAnswers: {},
  roomStatus: {},
  allRoomCompleted: false,
  isFinished: false,
  isMiddleState: false,
  totalRoomCompleted: 0,
  startedAt: null,
  shouldFinishAt: null,
  finishedAt: null,
  inventory: {},
  isGameScreenPopup: false,
  isTimeout: false,
};

const gameProgressSlice = createSlice({
  name: "gameProgress",
  initialState,
  reducers: {
    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['players']>} action
     */
    setPlayers: (state, action) => ({
      ...state,
      players: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['finalTeamAnswers']>} action
     */
    setFinalTeamAnswers: (state, action) => ({
      ...state,
      finalTeamAnswers: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['teamAnswers']>} action
     */
    setTeamAnswers: (state, action) => ({
      ...state,
      teamAnswers: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['roomStatus']>} action
     */
    setRoomStatus: (state, action) => ({
      ...state,
      roomStatus: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['totalRoomCompleted']>} action
     */
    setTotalRoomCompleted: (state, action) => ({
      ...state,
      totalRoomCompleted: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['allRoomCompleted']>} action
     */
    setAllRoomCompleted: (state, action) => ({
      ...state,
      allRoomCompleted: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isFinished']>} action
     */
    setFinished: (state, action) => ({
      ...state,
      isFinished: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isMiddleState']>} action
     */
    setIsMiddleState: (state, action) => ({
      ...state,
      isMiddleState: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['startedAt']>} action
     */
    setStartedAt: (state, action) => ({
      ...state,
      startedAt: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['shouldFinishAt']>} action
     */
    setShouldFinishAt: (state, action) => ({
      ...state,
      shouldFinishAt: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['finishedAt']>} action
     */
    setFinishedAt: (state, action) => ({
      ...state,
      finishedAt: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['inventory']>} action
     */
    setInventory: (state, action) => ({
      ...state,
      inventory: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<boolean>} action
     */
    setIsGameScreenPopup: (state, action) => ({
      ...state,
      isGameScreenPopup: action.payload,
    }),

    /**
     * @param {typeof initialState} state
     * @param {import("@reduxjs/toolkit").PayloadAction<boolean>} action
     */
    setIsGameTimeout: (state, action) => ({
      ...state,
      isTimeout: action.payload,
    }),
  },
});

export const getRoomStatus = (roomId) => {
  const roomStatus = getState().gameProgress.roomStatus;
  return roomStatus?.[roomId] || "is-status-open";
};

export const getFinalTeamAnswers = (roomId) => {
  const finalTeamAnswers = getState().gameProgress.finalTeamAnswers;
  return finalTeamAnswers?.[roomId] || false;
};

export const getTeamAnswers = (roomId) => {
  const teamAnswers = getState().gameProgress.teamAnswers;
  return teamAnswers?.[roomId] || false;
};

export const getInventory = () => {
  const inventory = getState().gameProgress.inventory;
  return inventory ?? [];
};

export const getPlayers = () => {
  const players = getState().gameProgress.players;
  return players ?? [];
};

export const getUserFromId = (userId) => {
  const players = getState().gameProgress.players;
  const user = players.find((p) => p.userId === userId);
  return user;
};

export const getTotalRoomCompleted = () => {
  const totalRoomCompleted = getState().gameProgress.totalRoomCompleted;
  return totalRoomCompleted;
};

export const getIsGameScreenPopup = () => {
  const isGameScreenPopup = getState().gameProgress.isGameScreenPopup;
  return isGameScreenPopup ?? false;
};

export const getIsTimeout = () => {
  const isTimeout = getState().gameProgress.isTimeout;
  return isTimeout ?? false;
};

export const {
  setPlayers,
  setTeamAnswers,
  setFinalTeamAnswers,
  setRoomStatus,
  setTotalRoomCompleted,
  setAllRoomCompleted,
  setFinished,
  setIsMiddleState,
  setStartedAt,
  setShouldFinishAt,
  setFinishedAt,
  setInventory,
  setIsGameScreenPopup,
  setIsGameTimeout,
} = gameProgressSlice.actions;
export const gameProgress = gameProgressSlice.reducer;
